import * as React from "react";
import { useDebounce } from "use-debounce";

import { NidusEntry } from "../../../../../../Common/NidusClient/INidusClient";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { InvalidProgramStateError } from "../../../../../../Common/Errors";
import { IContextualNidusClient } from "../../../../../../Common/NidusClient/ContextualNidusClient/IContextualNidusClient";
import { ContextualActionsProvider } from "../../../Internals/FormActionHandlers/ContextualActionsContext";

import { SelectEntryModal } from "./SelectEntryModal/SelectEntryModal";
import { UserFriendlyLoadingState, useUserFriendlyLoadingState } from "./SelectEntryModal/UserFriendlyLoadingState";

interface NidusColumn {
    title: string;
    width: string;
    name?: string;
    names?: string[];
}

interface SelectNidusEntryModalProps {
    nidusClient: IContextualNidusClient;
    title?: React.ReactNode;
    addButtonText?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    onRequestAddEntry: () => Promise<undefined | NidusEntry>;
    onRequestUpdateEntry: (entry: NidusEntry) => Promise<undefined | NidusEntry>;
    onRequestDeleteEntry: (entry: NidusEntry) => Promise<void>;
    onRequestRestoreItem: (entry: NidusEntry) => Promise<NidusEntry>;
    columns: NidusColumn[];
    onSelect: (item: NidusEntry) => void;
    onClose: () => void;
}

export function SelectNidusEntryModal(props: SelectNidusEntryModalProps): JSX.Element {
    const [searchString, setSearchString] = React.useState("");
    const [searchStringForRequest] = useDebounce(searchString, 300);

    const [isFirstRequestCompleted, setIsFirstRequestCompleted] = React.useState<boolean>(false);
    const [isSourceEmpty, setIsSourceEmpty] = React.useState<boolean | undefined>(undefined);
    const [loading, setLoading] = React.useState<boolean>(false);
    const userFriendlyLoadingState = useUserFriendlyLoadingState(loading);
    const [items, setItems] = React.useState<NidusEntry[]>([]);
    const [totalSuitableCount, setTotalSuitableCount] = React.useState<number>(0);
    const [removedItems, setRemovedItems] = React.useState<NidusEntry[]>([]);

    const columns = React.useMemo(
        () =>
            props.columns.map(column => ({
                title: column.title,
                width: column.width,
                value: (item: NidusEntry) => getValueValueForItem(column, item),
            })),
        [props.columns]
    );

    useAsyncEffect(
        async abortSignal => {
            setLoading(true);
            const response = await props.nidusClient.search(searchStringForRequest);
            if (searchStringForRequest === "" && isSourceEmpty == undefined) {
                setIsSourceEmpty(response.items.length === 0);
                setIsFirstRequestCompleted(true);
            }
            if (!abortSignal.aborted) {
                setItems(response.items);
                setTotalSuitableCount(response.totalSuitableCount);
            }
            setIsFirstRequestCompleted(true);
            setLoading(false);
        },
        [searchStringForRequest]
    );

    React.useEffect(() => {
        setItems(items.filter(item => !removedItems.includes(item)));
        setRemovedItems([]);
    }, [items.length]);

    const handleRequestItemUpdate = async (item: NidusEntry) => {
        const updatedItem = await props.onRequestUpdateEntry(item);
        if (updatedItem == undefined) {
            return;
        }
        const updatedItemIndex = items.findIndex(x => x.id === updatedItem.id);
        if (updatedItemIndex >= 0) {
            setItems([...items.slice(0, updatedItemIndex), updatedItem, ...items.slice(updatedItemIndex + 1)]);
        }
    };

    const handleRequestAddEntry = async () => {
        const createdEntry = await props.onRequestAddEntry();
        if (createdEntry != undefined) {
            setItems([createdEntry, ...items]);
            setTotalSuitableCount(totalSuitableCount + 1);
        }
    };

    const handleRequestItemDelete = async (item: NidusEntry) => {
        try {
            setLoading(true);
            await props.onRequestDeleteEntry(item);
            setRemovedItems([...removedItems, item]);
            setTotalSuitableCount(Math.max(totalSuitableCount - 1, 0));
        } finally {
            //"Операция завершилась неудачно. ");
            setLoading(false);
        }
    };

    const handleRequestRestoreItem = async (item: NidusEntry) => {
        try {
            setLoading(true);
            const updatedItem = await props.onRequestRestoreItem(item);

            const updatedItemIndex = items.findIndex(x => x.id === item.id);
            if (updatedItemIndex >= 0) {
                setItems([...items.slice(0, updatedItemIndex), updatedItem, ...items.slice(updatedItemIndex + 1)]);
                setTotalSuitableCount(totalSuitableCount + 1);
            }
            setRemovedItems(removedItems.filter(x => x !== item));
        } finally {
            //"Операция завершилась неудачно. ");
            setLoading(false);
        }
    };

    const contextualActions = React.useMemo(
        () => ({
            ["userPicklist.AddToReference"]: async () => {
                await handleRequestAddEntry();
            },
        }),
        []
    );

    return !isFirstRequestCompleted && userFriendlyLoadingState === UserFriendlyLoadingState.LoadingState1 ? (
        <></>
    ) : (
        <ContextualActionsProvider actions={contextualActions}>
            <SelectEntryModal
                loading={loading}
                searchString={searchString}
                onChangeSearchString={setSearchString}
                title={props.title}
                onRequestAddEntry={handleRequestAddEntry}
                addButtonText={props.addButtonText}
                onRequestItemUpdate={handleRequestItemUpdate}
                onRequestItemDelete={handleRequestItemDelete}
                onRequestRestoreItem={handleRequestRestoreItem}
                emptyMessage={props.emptyMessage}
                columns={columns}
                items={items}
                getItemId={item => item.id}
                removedItems={removedItems}
                onSelectItem={props.onSelect}
                onClose={props.onClose}
                showEmptyMessage={isSourceEmpty ?? false}
                restItemsMessage={
                    totalSuitableCount > items.length
                        ? `Показаны первые ${items.length} продавцов из ${totalSuitableCount}. Используйте поиск.`
                        : undefined
                }
            />
        </ContextualActionsProvider>
    );
}

function getValueValueForItem(column: NidusColumn, item: NidusEntry): string {
    if (column.name != undefined) {
        return item.data[column.name] ?? "";
    }
    if (column.names != undefined) {
        return column.names
            .map(x => item.data[x])
            .filter(x => x != undefined && x.trim() !== "")
            .join(" ");
    }
    throw new InvalidProgramStateError();
}
